import * as React from "react";
import { Clock, AlertTriangle } from "lucide-react";
import { cn } from "../../lib/utils";
import { getHoldingStaleness } from "../../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

/**
 * StalenessIndicator - Shows a visual warning for stale holdings
 *
 * Displays an icon with tooltip when a holding hasn't been updated
 * within the recommended timeframe based on its asset type.
 *
 * Thresholds:
 * - Cash holdings: 30 days
 * - Real Estate, Vehicles, Liabilities: 365 days
 *
 * @param {Object} props
 * @param {Object} props.holding - The raw holding object
 * @param {string} props.size - Icon size: 'sm' | 'md' | 'lg'
 * @param {boolean} props.showLabel - Whether to show text label
 * @param {string} props.className - Additional CSS classes
 */
export function StalenessIndicator({
  holding,
  size = "md",
  showLabel = false,
  className,
}) {
  const staleness = getHoldingStaleness(holding);

  // Don't render anything if not stale
  if (!staleness) {
    return null;
  }

  const sizeClasses = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  const iconSize = sizeClasses[size] || sizeClasses.md;

  // Use AlertTriangle for critical, Clock for warning
  const Icon = staleness.severity === "critical" ? AlertTriangle : Clock;

  const severityStyles = {
    warning: {
      icon: "text-amber-500",
      badge: "bg-amber-50 text-amber-700 border-amber-200",
      darkBadge: "dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-800",
    },
    critical: {
      icon: "text-red-500",
      badge: "bg-red-50 text-red-700 border-red-200",
      darkBadge: "dark:bg-red-950/50 dark:text-red-400 dark:border-red-800",
    },
  };

  const styles = severityStyles[staleness.severity] || severityStyles.warning;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1 cursor-help",
              showLabel && "rounded-full border px-2 py-0.5 text-xs font-medium",
              showLabel && styles.badge,
              showLabel && styles.darkBadge,
              className
            )}
            role="status"
            aria-label={staleness.message}
          >
            <Icon
              className={cn(iconSize, !showLabel && styles.icon)}
              aria-hidden="true"
            />
            {showLabel && (
              <span>
                {staleness.severity === "critical" ? "Needs Update" : "Stale"}
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-medium">
            {staleness.severity === "critical"
              ? "Update Required"
              : "Update Recommended"}
          </p>
          <p className="text-xs opacity-90 mt-0.5">{staleness.message}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * StaleBadge - Compact badge version of staleness indicator
 * Good for inline display in table cells
 */
export function StaleBadge({ holding, className }) {
  const staleness = getHoldingStaleness(holding);

  if (!staleness) {
    return null;
  }

  const severityStyles = {
    warning: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
    critical: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  };

  const Icon = staleness.severity === "critical" ? AlertTriangle : Clock;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium cursor-help",
              severityStyles[staleness.severity],
              className
            )}
            role="status"
            aria-label={staleness.message}
          >
            <Icon className="h-3 w-3" aria-hidden="true" />
            <span>Stale</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-medium">
            {staleness.severity === "critical"
              ? "Update Required"
              : "Update Recommended"}
          </p>
          <p className="text-xs opacity-90 mt-0.5">{staleness.message}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default StalenessIndicator;
